import { gql, useMutation } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import { datadogLogs } from '@datadog/browser-logs';
import type { TranslatedString } from '@interfaces/shared';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useInactivityTimeout } from '@providers/inActivityTimeoutProvider';
import { useAuthContext } from '@providers/ovApolloProvider';
import { UserContext } from '@providers/userContextProvider';
import { useSearchParams } from '@utils/usePageState';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

const TRANSITION_TRANSFER = gql`
  mutation transitionTransfer($input: TransitionTransferInput!) {
    transitionTransfer(input: $input) {
      transfer {
        id
      }
    }
  }
`;

/**
 * State machine for the external redirect/MFA verification flow.
 *
 *   LOADING ──► REDIRECTING ──► (external MFA page)
 *     │                              │
 *     │  ┌───────────────────────────┘
 *     │  │ (user returns via URL params)
 *     │  ▼
 *     ├─► SUCCESS ──► Close (onNext) or Cancel
 *     ├─► FAILURE ──► Retry (if retries remain) or Cancel
 *     ├─► CANCELLING ──► (auto-transitions via cancelTransfer → onNext)
 *     └─► CLOSED_TAB ──► Retry (if retries remain) or Cancel
 *
 *   retries (option):
 *     enabled=false → auto-cancel on first failure (toast + cancel)
 *     enabled=true, number=N → allow N retries, then auto-cancel
 */
type HandlerState =
  | 'LOADING'
  | 'REDIRECTING'
  | 'SUCCESS'
  | 'FAILURE'
  | 'CANCELLING'
  | 'CLOSED_TAB';

export type RedirectHandlerOptions = {
  title?: TranslatedString;
  subtitle?: TranslatedString;
  successTitle?: TranslatedString;
  successSubtitle?: TranslatedString;
  failureTitle?: TranslatedString;
  failureSubtitle?: TranslatedString;
  closedTabTitle?: TranslatedString;
  closedTabSubtitle?: TranslatedString;
  challengeUrl?: string;
  retries?: { number: number; enabled: boolean };
};

/** Parses the retries option into a stable { enabled, maxRetries } shape. */
export const parseRetriesOption = (
  retries?: RedirectHandlerOptions['retries'],
): { enabled: boolean; maxRetries: number } => ({
  enabled: retries?.enabled === true,
  maxRetries: retries?.enabled === true ? (retries.number ?? 0) : 0,
});

type RedirectHandlerProps = {
  workflowCompletion: { id: string };
  onNext: (fromKey?: string) => void;
  options: RedirectHandlerOptions;
};

type RedirectMarker = {
  transactionId: string;
  redirectedAt: string;
};

const STORAGE_KEY_PREFIX = 'redirectHandler_';

const getRedirectMarker = (
  workflowCompletionId: string,
): RedirectMarker | null => {
  try {
    const raw = window.localStorage.getItem(
      `${STORAGE_KEY_PREFIX}${workflowCompletionId}`,
    );
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    datadogLogs.logger.warn('RedirectHandler: failed to read redirect marker', {
      err,
      workflowCompletionId,
    });
    return null;
  }
};

const setRedirectMarker = (
  workflowCompletionId: string,
  marker: RedirectMarker,
) => {
  try {
    window.localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${workflowCompletionId}`,
      JSON.stringify(marker),
    );
  } catch (err) {
    datadogLogs.logger.warn('RedirectHandler: failed to set redirect marker', {
      err,
      workflowCompletionId,
    });
  }
};

const clearRedirectMarker = (workflowCompletionId: string) => {
  try {
    window.localStorage.removeItem(
      `${STORAGE_KEY_PREFIX}${workflowCompletionId}`,
    );
  } catch (err) {
    datadogLogs.logger.warn(
      'RedirectHandler: failed to clear redirect marker',
      { err, workflowCompletionId },
    );
  }
};

/**
 * Resolve OTP status from URL query params.
 * Priority: `ov_challenge_status` (generic) > `atbmeta`/`status` (ATB-specific).
 */
const resolveRedirectStatus = (): {
  status: 'SUCCESS' | 'FAILURE';
  transactionId?: string;
} | null => {
  const params = new URLSearchParams(window.location.search);

  const ovStatus = params.get('ov_challenge_status')?.toUpperCase();
  if (ovStatus === 'SUCCESS' || ovStatus === 'FAILURE') {
    return { status: ovStatus };
  }

  const atbMeta = params.get('atbmeta') || '';
  if (!atbMeta) return null;

  let status: string | undefined;
  let transactionId: string | undefined;

  if (atbMeta.includes('|')) {
    const metadata = atbMeta.split('|');
    status = metadata
      .find((e) => e.startsWith('status:'))
      ?.split(':')[1]
      ?.toUpperCase();
    transactionId = metadata
      .find((e) => e.startsWith('transactionId:'))
      ?.split(':')[1];
  } else {
    const transactionIdMatch = atbMeta.match(/transactionId:([^&]+)/);
    transactionId = transactionIdMatch?.[1];
    status = (params.get('status') || '').toUpperCase();
  }

  if (status === 'SUCCESS' || status === 'FAILURE') {
    return { status, transactionId };
  }

  return null;
};

const REDIRECT_PARAMS = ['ov_challenge_status', 'atbmeta', 'status'];

export const RedirectHandler = ({
  workflowCompletion,
  onNext,
  options,
}: RedirectHandlerProps) => {
  const { t } = useTranslation('workflowCompletions');
  const { custodianConnection } = useContext(UserContext);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);
  const { attemptSilentRefresh } = useInactivityTimeout();
  const { settings } = useAuthContext();
  const [, setSearchParams] = useSearchParams();
  const { showToast } = useGlobalToast();
  const [state, setState] = useState<HandlerState>('LOADING');
  const processedRef = useRef(false);

  const [transitionTransfer] = useMutation(TRANSITION_TRANSFER);

  const { enabled: retriesEnabled, maxRetries } = parseRetriesOption(
    options?.retries,
  );
  const workflowCompletionId: string = workflowCompletion?.id;

  const ctxData = workflowData?.custodianDepositTransfer;

  const retryCount = ctxData?.challengeResult?.retryCount ?? 0;
  const retriesExhausted =
    !retriesEnabled || maxRetries <= 0 || retryCount >= maxRetries;

  const currentTransferId: string | undefined = workflowData?.currentTransferId;

  const logContext = useMemo(
    () => ({
      workflowCompletionId,
      transferId: currentTransferId,
      custodian: custodianConnection?.name,
    }),
    [workflowCompletionId, currentTransferId, custodianConnection?.name],
  );

  const cancelTransfer = useCallback(
    async (toastOverride?: string) => {
      if (!currentTransferId) {
        datadogLogs.logger.warn(
          'RedirectHandler: no currentTransferId to cancel',
          { ...logContext },
        );
        onNext();
        return;
      }
      try {
        await transitionTransfer({
          variables: {
            input: { transferId: currentTransferId, transition: 'cancel' },
          },
        });
        datadogLogs.logger.info(
          'RedirectHandler: transfer cancelled successfully',
          { ...logContext, event: 'cancel_complete' },
        );
        showToast({
          message: toastOverride ?? t('redirectHandler.cancelSuccess'),
          severity: toastOverride ? 'error' : 'success',
        });
      } catch (err) {
        datadogLogs.logger.error('RedirectHandler: transitionTransfer failed', {
          ...logContext,
          err,
        });
        showToast({
          message: t('redirectHandler.cancelError'),
          severity: 'error',
        });
      }
      onNext();
    },
    [currentTransferId, transitionTransfer, onNext, showToast, t, logContext],
  );

  const saveChallengeResult = useCallback(
    async (
      status: 'SUCCESS' | 'FAILURE' | 'RETRYING',
      transactionId?: string,
      currentRetryCount?: number,
    ) => {
      await setWorkflowData((prev) => ({
        ...prev,
        custodianDepositTransfer: {
          ...prev.custodianDepositTransfer,
          challengeResult: {
            status,
            transactionId,
            retryCount: currentRetryCount ?? 0,
            processedAt: new Date().toISOString(),
          },
        },
      }));
    },
    [setWorkflowData],
  );

  const triggerRedirect = useCallback(() => {
    const response = ctxData?.response;
    const transactionId = response?.action?.details?.find(
      (d) => d.key === 'transactionId',
    )?.value as string | undefined;

    const challengeUrl =
      options?.challengeUrl ||
      (custodianConnection?.metadata?.find((m: any) => m.key === 'challengeUrl')
        ?.value as string);

    if (!challengeUrl || !transactionId) {
      datadogLogs.logger.error(
        'RedirectHandler: missing challengeUrl or transactionId',
        {
          ...logContext,
          hasChallengeUrl: !!challengeUrl,
          hasTransactionId: !!transactionId,
        },
      );
      setState('FAILURE');
      return;
    }

    // TODO (OVT-6217): `cmid` is always null after the Simility removal — no
    // code path writes it to sessionStorage so triggerRedirect should not fire
    // in practice until the replacement fraud engine is wired up. At that point,
    // we can do any needed refactor with applicable context/details.
    const cmid = window.sessionStorage.getItem('CMID');

    // Skip silent refresh for hosted login — it can trigger an interactive
    // re-authentication prompt (e.g. ATB passwordless code) before the redirect.
    if (attemptSilentRefresh && !settings?.useHostedLogin) {
      attemptSilentRefresh();
    }

    setRedirectMarker(workflowCompletionId, {
      transactionId,
      redirectedAt: new Date().toISOString(),
    });

    // The return URL embeds the transactionId in atbmeta so the handler can
    // correlate the response with the correct challenge on return.
    const inboundUrl = new URL(
      window.location.pathname,
      window.location.origin,
    );
    inboundUrl.searchParams.set('workflowCompletion', workflowCompletionId);
    inboundUrl.searchParams.set('atbmeta', `transactionId:${transactionId}`);

    // ATB's MFA page expects the entire query string URI-encoded as a single
    // value appended to the challenge URL — this is an ATB-specific contract,
    // not a standard query-string format.
    const toEncode = `session-id=${cmid ?? ''}&transaction-id=${transactionId}&redirect=${inboundUrl.toString()}`;
    const outboundRedirectUrl = `${challengeUrl}?${encodeURIComponent(toEncode)}`;

    datadogLogs.logger.info('RedirectHandler: redirecting to external MFA', {
      ...logContext,
      event: 'redirect_out',
      transactionId,
    });

    setState('REDIRECTING');
    window.location.replace(outboundRedirectUrl);
  }, [
    ctxData,
    options,
    custodianConnection,
    workflowCompletionId,
    attemptSilentRefresh,
    settings,
    logContext,
  ]);

  const failureToastMessage =
    translateBackend(options?.failureTitle) ||
    t('redirectHandler.failureTitle');

  const autoCancelWithToast = useCallback(async () => {
    setState('CANCELLING');
    clearRedirectMarker(workflowCompletionId);
    await cancelTransfer(failureToastMessage);
  }, [workflowCompletionId, cancelTransfer, failureToastMessage]);

  const handleFailure = useCallback(async () => {
    if (retriesExhausted) {
      datadogLogs.logger.warn(
        'RedirectHandler: retries exhausted, auto-cancelling',
        { ...logContext, event: 'auto_cancel', retryCount, maxRetries },
      );
      await autoCancelWithToast();
    } else {
      setState('FAILURE');
    }
  }, [
    retriesExhausted,
    autoCancelWithToast,
    logContext,
    retryCount,
    maxRetries,
  ]);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const process = async () => {
      const redirectResult = resolveRedirectStatus();
      if (redirectResult) {
        datadogLogs.logger.info('RedirectHandler: returned from external MFA', {
          ...logContext,
          event: 'redirect_return',
          status: redirectResult.status,
          transactionId: redirectResult.transactionId,
        });
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            for (const p of REDIRECT_PARAMS) next.delete(p);
            return next;
          },
          { replace: true, preserveHash: true },
        );
        clearRedirectMarker(workflowCompletionId);
        try {
          await saveChallengeResult(
            redirectResult.status,
            redirectResult.transactionId,
            retryCount,
          );
        } catch (err) {
          datadogLogs.logger.error(
            'RedirectHandler: failed to persist challenge result',
            { ...logContext, err },
          );
        }

        if (redirectResult.status === 'SUCCESS') {
          setState('SUCCESS');
        } else {
          await handleFailure();
        }
        return;
      }

      const marker = getRedirectMarker(workflowCompletionId);
      if (marker) {
        datadogLogs.logger.info(
          'RedirectHandler: user returned without completing MFA',
          { ...logContext, event: 'closed_tab_detected' },
        );
        setState('CLOSED_TAB');
        return;
      }

      const response = ctxData?.response;
      const existingResult = ctxData?.challengeResult;

      if (response?.status === 'CHALLENGE') {
        const challengeTxId = response.action?.details?.find(
          (d: { key: string }) => d.key === 'transactionId',
        )?.value as string | undefined;

        if (
          !existingResult ||
          (challengeTxId && challengeTxId !== existingResult.transactionId)
        ) {
          triggerRedirect();
          return;
        }
        if (existingResult.status === 'SUCCESS') {
          setState('SUCCESS');
        } else {
          await handleFailure();
        }
        return;
      }

      if (existingResult) {
        if (existingResult.status === 'SUCCESS') {
          setState('SUCCESS');
        } else {
          await handleFailure();
        }
        return;
      }

      if (response?.status === 'SUCCESS') {
        setState('SUCCESS');
        return;
      }

      if (response?.status === 'FAILURE') {
        setState('CANCELLING');
        await cancelTransfer();
        return;
      }

      // None of the conditions matched: the workflow context has not been
      // populated yet. Unlock processedRef so the effect re-fires when
      // workflowData arrives and can resolve to the correct state.
      processedRef.current = false;
    };

    process();
  }, [
    ctxData,
    retryCount,
    workflowCompletionId,
    saveChallengeResult,
    handleFailure,
    triggerRedirect,
    cancelTransfer,
    logContext,
    setSearchParams,
  ]);

  const handleRetry = async () => {
    const nextRetryCount = retryCount + 1;
    datadogLogs.logger.info('RedirectHandler: user retrying verification', {
      ...logContext,
      event: 'retry_click',
      retryCount: nextRetryCount,
    });
    await saveChallengeResult('RETRYING', undefined, nextRetryCount);
    clearRedirectMarker(workflowCompletionId);
    triggerRedirect();
  };

  const handleCancel = async () => {
    datadogLogs.logger.info('RedirectHandler: user cancelled transfer', {
      ...logContext,
      event: 'cancel_click',
    });
    setState('CANCELLING');
    clearRedirectMarker(workflowCompletionId);
    await cancelTransfer();
  };

  const handleClose = () => {
    onNext();
  };

  const { title, subtitle } = useMemo(() => {
    switch (state) {
      case 'LOADING':
      case 'REDIRECTING':
        return {
          title:
            translateBackend(options?.title) ||
            t('redirectHandler.redirecting'),
          subtitle: translateBackend(options?.subtitle) || '',
        };
      case 'SUCCESS':
        return {
          title:
            translateBackend(options?.successTitle) ||
            t('redirectHandler.successTitle'),
          subtitle:
            translateBackend(options?.successSubtitle) ||
            t('redirectHandler.successSubtitle'),
        };
      case 'FAILURE':
      case 'CANCELLING':
        return {
          title:
            translateBackend(options?.failureTitle) ||
            t('redirectHandler.failureTitle'),
          subtitle:
            state === 'CANCELLING'
              ? t('redirectHandler.cancelling')
              : translateBackend(options?.failureSubtitle) ||
                t('redirectHandler.failureSubtitle'),
        };
      case 'CLOSED_TAB':
        return {
          title:
            translateBackend(options?.closedTabTitle) ||
            t('redirectHandler.closedTabTitle'),
          subtitle:
            translateBackend(options?.closedTabSubtitle) ||
            t('redirectHandler.closedTabSubtitle'),
        };
    }
  }, [state, options, t]);

  const showSpinner =
    state === 'LOADING' || state === 'REDIRECTING' || state === 'CANCELLING';
  const showRetryActions = state === 'FAILURE' || state === 'CLOSED_TAB';

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      gap={2}
      minHeight='300px'
    >
      <Typography variant='headingMedium' sx={{ textAlign: 'center' }}>
        {title}
      </Typography>

      {subtitle && (
        <Typography variant='bodyMedium' sx={{ mb: 2, textAlign: 'center' }}>
          {subtitle}
        </Typography>
      )}

      {showSpinner && <CircularProgress />}

      {state === 'SUCCESS' && (
        <Box display='flex' gap={2} mt={2}>
          <Button
            label={t('redirectHandler.close')}
            onClick={handleClose}
            variant='filled'
          />
          <Button
            label={t('redirectHandler.cancel')}
            onClick={handleCancel}
            variant='text'
          />
        </Box>
      )}

      {showRetryActions && (
        <Box display='flex' gap={2} mt={2}>
          <Button
            label={t('redirectHandler.retry')}
            onClick={handleRetry}
            variant='filled'
          />
          <Button
            label={t('redirectHandler.cancel')}
            onClick={handleCancel}
            variant='text'
          />
        </Box>
      )}
    </Box>
  );
};
